import React, { useEffect, useMemo, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { FacilitiesAdmin } from "@/components/FacilitiesAdmin";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, Ban, CheckCircle2, KeyRound, Download, Search, XCircle, Pencil, Check, X, Radio, ListChecks, Trash2, Save } from "lucide-react";

const ROLE_COLOR = {
  admin: "#F59E0B", supervisor: "#8B5CF6", watch_commander: "#8B5CF6",
  records_supervisor: "#8B5CF6", police_dispatcher: "#3B82F6",
  fire_dispatcher: "#EF4444", ems_dispatcher: "#10B981",
  field_unit: "#10B981", pa_state_constable: "#22D3EE", call_taker: "#9CA3AF",
};
const DISC_COLOR = { police: "#3B82F6", fire: "#EF4444", ems: "#10B981" };
const RANKS = ["Officer", "Corporal", "Sergeant", "Lieutenant", "Captain", "Major", "Deputy Chief", "Chief",
  "Constable", "Deputy Constable", "Dispatcher", "Telecommunicator", "Director", "Civilian"];

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState({ name: "", email: "", username: "", password: "", role: "pa_state_constable", rank: "", badge: "", env: "live" });
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [unitForm, setUnitForm] = useState({ unit_id: "", type: "police", personnel: "", env: "live" });
  const [customTypes, setCustomTypes] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [ctForm, setCtForm] = useState({ label: "", discipline: "police", priority: 3, category: "CUSTOM", dispatch: "", questions: [], pre_arrival: [] });
  const [ctEditCode, setCtEditCode] = useState(null);
  const [dups, setDups] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);

  const scanDups = async () => {
    setDupBusy(true);
    try { const { data } = await client.get("/units/duplicates"); setDups(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDupBusy(false); }
  };
  const removeUnit = async (uid) => {
    if (!window.confirm(`Remove unit record ${uid}? The removal is audited and the record is preserved in the audit log.`)) return;
    try {
      await client.delete(`/units/${uid}`);
      toast.success(`Unit ${uid} removed (audited)`);
      scanDups();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const mergeUnits = async (keepId, removeId) => {
    if (!window.confirm(`MERGE: keep ${keepId}, fold ${removeId} into it?\n\nAll incident history on ${removeId} moves to ${keepId}. The merge is fully audited.`)) return;
    try {
      const { data } = await client.post("/units/merge", { keep_unit_id: keepId, remove_unit_id: removeId });
      const moved = data.history_moved || {};
      toast.success(`${removeId} merged into ${keepId} — history moved (${moved.units_history || 0} calls)`, { duration: 8000 });
      scanDups();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = async () => {
    const [u, r] = await Promise.all([client.get("/admin/users"), client.get("/admin/roles")]);
    setUsers(u.data); setRoles(r.data.roles);
  };
  const loadCallTypes = async () => {
    const [cust, all] = await Promise.all([client.get("/admin/call-types"), client.get("/call-types")]);
    setCustomTypes(cust.data); setAllTypes(all.data || []);
  };
  useEffect(() => { load(); loadCallTypes(); }, []);

  const pending = useMemo(() => users.filter((u) => u.approval_status === "pending"), [users]);
  const active = useMemo(() => users.filter((u) => u.approval_status !== "pending"), [users]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return active;
    return active.filter((u) => [u.name, u.username, u.email, u.badge, u.agency].some((v) => (v || "").toLowerCase().includes(q)));
  }, [active, query]);

  const create = async () => {
    if (!form.name || !form.role) { toast.error("Name and role are required"); return; }
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
    try {
      const { data } = await client.post("/admin/users", payload);
      if (data.initial_access_code)
        toast.success(`${data.name} (@${data.username}) — access code ${data.initial_access_code}`, { duration: 12000 });
      else
        toast.success(`${data.name} (@${data.username}) provisioned`);
      setForm({ name: "", email: "", username: "", password: "", role: "pa_state_constable", rank: "", badge: "", env: "live" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setActive = async (id, activeVal) => {
    await client.patch(`/admin/users/${id}`, { active: activeVal });
    toast.success(activeVal ? "Account reactivated" : "Account restricted — sign-in blocked"); load();
  };
  const startEdit = (u) => { setEditId(u.id); setEditName(u.name); };
  const saveName = async (id) => {
    if (!editName.trim()) { toast.error("Name cannot be empty"); return; }
    await client.patch(`/admin/users/${id}`, { name: editName.trim() });
    toast.success("Name updated"); setEditId(null); load();
  };
  const renameCallsign = async (u) => {
    const nv = window.prompt(`New call sign / unit ID for ${u.name} (currently "${u.badge || "—"}").\nThis renames the user badge, the dispatchable unit, and every call, message and GPS reference.`, u.badge || "");
    if (nv == null || !nv.trim() || nv.trim() === u.badge) return;
    try {
      const { data } = await client.post(`/admin/users/${u.id}/rename-callsign`, { new_callsign: nv.trim() });
      toast.success(`${data.old} → ${data.new} · ${Object.values(data.updated).reduce((a, b) => a + b, 0)} records updated`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Rename failed"); }
  };
  const changeRole = async (id, role) => {
    await client.patch(`/admin/users/${id}`, { role }); toast.success("Role updated"); load();
  };
  const changeRank = async (id, rank) => {
    await client.patch(`/admin/users/${id}`, { rank }); toast.success("Rank updated"); load();
  };
  const approve = async (id) => { await client.post(`/admin/users/${id}/approve`); toast.success("Account approved"); load(); };
  const reject = async (id) => { await client.post(`/admin/users/${id}/reject`); toast("Account rejected"); load(); };
  const regenCode = async (id, name) => {
    const { data } = await client.post(`/admin/users/${id}/reset-code`);
    toast.success(`${name} — new access code ${data.access_code}`, { duration: 12000 });
    load();
  };
  const exportAudit = async () => {
    try {
      const r = await client.get("/admin/audit/ncic.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "ncic_audit_log.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("NCIC audit log exported");
    } catch { toast.error("Export failed"); }
  };
  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast("Access code copied"); }
    catch { toast(`Access code: ${code}`); }
  };

  const inp = "mt-1 w-full bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-sm outline-none focus:border-[#3B82F6] font-mono";

  const createUnit = async () => {
    if (!unitForm.unit_id.trim()) { toast.error("Unit ID / call sign required"); return; }
    try {
      await client.post("/admin/units", unitForm);
      toast.success(`Unit ${unitForm.unit_id} added to ${unitForm.env} — now available for dispatch`);
      setUnitForm({ unit_id: "", type: "police", personnel: "", env: "live" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetCt = () => { setCtForm({ label: "", discipline: "police", priority: 3, category: "CUSTOM", dispatch: "", questions: [], pre_arrival: [] }); setCtEditCode(null); };
  const cloneFromType = async (code) => {
    if (!code) return;
    try {
      const { data } = await client.get(`/call-types/${code}`);
      setCtForm((f) => ({ ...f, questions: data.questions || [], pre_arrival: data.pre_arrival || [] }));
      toast.success(`Copied questions & pre-arrival from ${data.label}`);
    } catch { toast.error("Could not copy from that call type"); }
  };
  const saveCallType = async () => {
    if (!ctForm.label.trim()) { toast.error("Call type label required"); return; }
    try {
      if (ctEditCode) { await client.patch(`/admin/call-types/${ctEditCode}`, ctForm); toast.success("Call type updated"); }
      else { await client.post("/admin/call-types", ctForm); toast.success(`Call type "${ctForm.label}" added`); }
      resetCt(); loadCallTypes();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const editCallType = (c) => { setCtEditCode(c.code); setCtForm({ label: c.label, discipline: c.discipline, priority: c.priority, category: c.category, dispatch: c.dispatch || "", questions: c.questions || [], pre_arrival: c.pre_arrival || [] }); };
  const deleteCallType = async (code) => {
    try { await client.delete(`/admin/call-types/${code}`); toast.success("Call type removed"); if (ctEditCode === code) resetCt(); loadCallTypes(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Layout>
      <PageHeader title="Administration — User Provisioning" sub="Create & manage operator accounts · role-based access (CJIS) · self-registrations require approval"
        right={<button data-testid="export-audit-btn" onClick={exportAudit}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-head bg-[#1E1F24] border border-[#2C2D35] hover:border-[#F59E0B] hover:text-[#F59E0B] transition-colors">
          <Download size={13} /> Export NCIC Audit</button>} />
      <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr]">
        {/* create form */}
        <div className="border-r border-[#2C2D35] p-4 bg-[#0d0e11]">
          <div className="flex items-center gap-2 mb-3 text-[#F59E0B]"><UserPlus size={15} /><span className="font-head font-semibold text-sm">PROVISION NEW ACCOUNT</span></div>
          <div className="space-y-2">
            {[["name", "Full name"], ["username", "Username (blank = first initial + last name)"], ["email", "Email (optional)"], ["password", "Passphrase (blank = generate 7-digit code)"], ["badge", "Badge / Call Sign / Unit ID"]].map(([k, ph]) => (
              <div key={k}>
                <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">{ph}</label>
                <input data-testid={`admin-input-${k}`} type={k === "password" ? "text" : "text"} value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inp} />
              </div>
            ))}
            <div>
              <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Rank / Title</label>
              <select data-testid="admin-select-rank" value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} className={inp}>
                <option value="">— No rank —</option>
                {RANKS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Assign role</label>
              <select data-testid="admin-select-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inp}>
                {roles.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Environment</label>
              <select data-testid="admin-select-env" value={form.env} onChange={(e) => setForm({ ...form, env: e.target.value })} className={inp}>
                <option value="live">Live (production)</option>
                <option value="training">Training (isolated sandbox)</option>
              </select>
              {form.env === "training" && <div className="font-mono text-[10px] text-[#F59E0B] mt-1">Trainee sees only simulated calls — isolated from live operations.</div>}
            </div>
            <button data-testid="admin-create-user-btn" onClick={create}
              className="w-full flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563eb] text-white font-head font-semibold py-2.5 text-sm transition-colors">
              <ShieldCheck size={15} /> Create Account
            </button>
          </div>

          {/* add unit */}
          <div className="mt-6 pt-4 border-t border-[#2C2D35]">
            <div className="flex items-center gap-2 mb-3 text-[#10B981]"><Radio size={15} /><span className="font-head font-semibold text-sm">ADD DISPATCHABLE UNIT</span></div>
            <div className="space-y-2">
              <div>
                <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Unit ID / Call sign</label>
                <input data-testid="admin-unit-id" value={unitForm.unit_id} onChange={(e) => setUnitForm({ ...unitForm, unit_id: e.target.value })} className={inp} placeholder="e.g. MOPS-24" />
              </div>
              <div>
                <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Personnel (optional)</label>
                <input data-testid="admin-unit-personnel" value={unitForm.personnel} onChange={(e) => setUnitForm({ ...unitForm, personnel: e.target.value })} className={inp} placeholder="Officer name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Discipline</label>
                  <select data-testid="admin-unit-type" value={unitForm.type} onChange={(e) => setUnitForm({ ...unitForm, type: e.target.value })} className={inp}>
                    <option value="police">Police</option><option value="fire">Fire</option><option value="ems">EMS</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Environment</label>
                  <select data-testid="admin-unit-env" value={unitForm.env} onChange={(e) => setUnitForm({ ...unitForm, env: e.target.value })} className={inp}>
                    <option value="live">Live</option><option value="training">Training</option>
                  </select>
                </div>
              </div>
              <button data-testid="admin-create-unit-btn" onClick={createUnit}
                className="w-full flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#059669] text-black font-head font-semibold py-2.5 text-sm transition-colors">
                <Radio size={15} /> Add Unit to Roster
              </button>
            </div>
          </div>

          {/* Duplicate unit detector — flags likely dup roster records (format variants, same personnel) */}
          <div className="mt-6 pt-4 border-t border-[#2C2D35]" data-testid="admin-dup-units">
            <div className="flex items-center gap-2 mb-2 text-[#F59E0B]"><Search size={15} /><span className="font-head font-semibold text-sm">DUPLICATE UNIT DETECTOR</span></div>
            <button data-testid="admin-dup-scan-btn" onClick={scanDups} disabled={dupBusy}
              className="w-full py-2 text-xs font-head bg-[#1E1F24] border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors disabled:opacity-50">
              {dupBusy ? "Scanning…" : "Scan Roster for Duplicates"}
            </button>
            {dups && dups.duplicates.length === 0 && (
              <div className="mt-2 text-[11px] font-mono text-[#10B981]">✓ No likely duplicates found ({dups.scanned} units scanned).</div>
            )}
            {dups && dups.duplicates.map((p, i) => (
              <div key={i} data-testid={`admin-dup-pair-${i}`} className="mt-2 p-2 border border-[#F59E0B]/50 bg-[#F59E0B]/[0.05]">
                <div className="font-mono text-[11px] text-white">
                  <b>{p.unit_a.unit_id}</b> ({p.unit_a.personnel || "—"}) ↔ <b>{p.unit_b.unit_id}</b> ({p.unit_b.personnel || "—"})
                </div>
                <div className="font-mono text-[10px] text-[#F59E0B] mt-0.5">{p.reasons.join(" · ")}</div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  <button data-testid={`admin-dup-merge-keep-${p.unit_a.unit_id}`}
                    disabled={!!p.unit_b.current_incident}
                    title={p.unit_b.current_incident ? `${p.unit_b.unit_id} is on an active call — clear it first` : `Keep ${p.unit_a.unit_id}, move ${p.unit_b.unit_id}'s history into it`}
                    onClick={() => mergeUnits(p.unit_a.unit_id, p.unit_b.unit_id)}
                    className="px-2 py-1 text-[10px] font-head border border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 transition-colors disabled:opacity-30">
                    ⇤ Merge → keep {p.unit_a.unit_id}
                  </button>
                  <button data-testid={`admin-dup-merge-keep-${p.unit_b.unit_id}`}
                    disabled={!!p.unit_a.current_incident}
                    title={p.unit_a.current_incident ? `${p.unit_a.unit_id} is on an active call — clear it first` : `Keep ${p.unit_b.unit_id}, move ${p.unit_a.unit_id}'s history into it`}
                    onClick={() => mergeUnits(p.unit_b.unit_id, p.unit_a.unit_id)}
                    className="px-2 py-1 text-[10px] font-head border border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 transition-colors disabled:opacity-30">
                    Merge → keep {p.unit_b.unit_id} ⇥
                  </button>
                  {[p.unit_a, p.unit_b].map((u) => (
                    <button key={u.unit_id} data-testid={`admin-dup-remove-${u.unit_id}`}
                      disabled={!!u.current_incident}
                      title={u.current_incident ? `On active call ${u.current_incident} — cannot remove` : "Remove this unit record (audited)"}
                      onClick={() => removeUnit(u.unit_id)}
                      className="px-2 py-1 text-[10px] font-head border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/10 transition-colors disabled:opacity-30 flex items-center gap-1">
                      <Trash2 size={11} /> Remove {u.unit_id}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* custom CFS call types */}
          <div className="mt-6 pt-4 border-t border-[#2C2D35]" data-testid="admin-call-types">
            <div className="flex items-center gap-2 mb-3 text-[#0EA5E9]"><ListChecks size={15} /><span className="font-head font-semibold text-sm">CFS CALL TYPES {ctEditCode && <span className="text-[#9CA3AF]">· editing</span>}</span></div>
            <div className="space-y-2">
              <div>
                <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Call type label</label>
                <input data-testid="ct-label" value={ctForm.label} onChange={(e) => setCtForm({ ...ctForm, label: e.target.value })} className={inp} placeholder="e.g. Community Event Detail" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Discipline</label>
                  <select data-testid="ct-discipline" value={ctForm.discipline} onChange={(e) => setCtForm({ ...ctForm, discipline: e.target.value })} className={inp}>
                    <option value="police">Police</option><option value="fire">Fire</option><option value="ems">EMS</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Priority</label>
                  <select data-testid="ct-priority" value={ctForm.priority} onChange={(e) => setCtForm({ ...ctForm, priority: Number(e.target.value) })} className={inp}>
                    <option value={0}>P0</option><option value={1}>P1</option><option value={2}>P2</option>
                    <option value={3}>P3</option><option value={4}>P4</option><option value={5}>P5</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Category</label>
                <input data-testid="ct-category" value={ctForm.category} onChange={(e) => setCtForm({ ...ctForm, category: e.target.value })} className={inp} placeholder="CUSTOM" />
              </div>
              <div>
                <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Dispatch protocol (optional)</label>
                <textarea data-testid="ct-dispatch" value={ctForm.dispatch} onChange={(e) => setCtForm({ ...ctForm, dispatch: e.target.value })} className={inp + " h-16 resize-none"} placeholder="How to dispatch units for this call…" />
              </div>
              <div>
                <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Copy questions from an existing call type (optional)</label>
                <select data-testid="ct-clone-source" defaultValue="" onChange={(e) => { cloneFromType(e.target.value); e.target.value = ""; }} className={inp}>
                  <option value="">— Select a template to copy from —</option>
                  {allTypes.map((t) => <option key={t.code} value={t.code}>{t.label} ({t.code})</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Caller interrogation questions — one per line</label>
                <textarea data-testid="ct-questions" value={(ctForm.questions || []).join("\n")} onChange={(e) => setCtForm({ ...ctForm, questions: e.target.value.split("\n") })} className={inp + " h-20 resize-none"} placeholder={"Is anyone injured?\nAre weapons involved?\nWhere is the subject now?"} />
              </div>
              <div>
                <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Pre-arrival instructions — one per line</label>
                <textarea data-testid="ct-prearrival" value={(ctForm.pre_arrival || []).join("\n")} onChange={(e) => setCtForm({ ...ctForm, pre_arrival: e.target.value.split("\n") })} className={inp + " h-20 resize-none"} placeholder={"Keep the caller on the line.\nMove to a safe location.\nDo not touch anything at the scene."} />
              </div>
              <div className="flex gap-2">
                <button data-testid="ct-save-btn" onClick={saveCallType}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#0EA5E9] hover:bg-[#0284c7] text-white font-head font-semibold py-2.5 text-sm transition-colors">
                  {ctEditCode ? <><Save size={15} /> Update Call Type</> : <><ListChecks size={15} /> Add Call Type</>}
                </button>
                {ctEditCode && <button data-testid="ct-cancel-btn" onClick={resetCt} className="px-3 bg-[#1E1F24] border border-[#2C2D35] text-[#9CA3AF] font-head text-sm"><X size={14} /></button>}
              </div>
            </div>
            <div className="mt-3 space-y-1">
              {customTypes.length === 0 && <div className="font-mono text-[10px] text-[#6B7280]">No custom call types yet.</div>}
              {customTypes.map((c) => (
                <div key={c.code} data-testid={`ct-row-${c.code}`} className="flex items-center gap-2 bg-[#141518] border border-[#2C2D35] px-2 py-1.5">
                  <span className="font-mono text-[9px] px-1 py-0.5 border" style={{ color: DISC_COLOR[c.discipline], borderColor: DISC_COLOR[c.discipline] }}>P{c.priority}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-head text-xs truncate">{c.label}</div>
                    <div className="font-mono text-[9px] text-[#6B7280]">{c.discipline} · {c.category} · {c.code}</div>
                  </div>
                  <button data-testid={`ct-edit-${c.code}`} onClick={() => editCallType(c)} className="text-[#6B7280] hover:text-[#0EA5E9]"><Pencil size={12} /></button>
                  <button data-testid={`ct-delete-${c.code}`} onClick={() => deleteCallType(c.code)} className="text-[#6B7280] hover:text-[#EF4444]"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* right */}
        <div className="overflow-y-auto p-4">
          {/* pending approvals */}
          {pending.length > 0 && (
            <div data-testid="pending-approvals" className="mb-5 border border-[#F59E0B]/40 bg-[#F59E0B]/5">
              <div className="px-3 py-2 font-head text-xs font-semibold text-[#F59E0B] border-b border-[#F59E0B]/30">PENDING APPROVAL ({pending.length})</div>
              {pending.map((u) => (
                <div key={u.id} data-testid={`pending-${u.email}`} className="flex items-center gap-3 px-3 py-2 border-b border-[#2C2D35] text-sm">
                  <div className="flex-1"><span className="font-head">{u.name}</span> <span className="font-mono text-[11px] text-[#9CA3AF]">@{u.username} · {u.email}</span></div>
                  <button data-testid={`approve-${u.email}`} onClick={() => approve(u.id)} className="flex items-center gap-1 px-2 py-1 text-[11px] font-head border border-[#2C2D35] hover:border-[#10B981] hover:text-[#10B981]"><CheckCircle2 size={12} /> Approve</button>
                  <button data-testid={`reject-${u.email}`} onClick={() => reject(u.id)} className="flex items-center gap-1 px-2 py-1 text-[11px] font-head border border-[#2C2D35] hover:border-[#EF4444] hover:text-[#EF4444]"><XCircle size={12} /> Reject</button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mb-3 bg-[#141518] border border-[#2C2D35] px-2 max-w-sm">
            <Search size={13} className="text-[#9CA3AF]" />
            <input data-testid="admin-user-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, username, badge, county…" className="flex-1 bg-transparent py-1.5 text-sm outline-none font-mono" />
          </div>

          <table className="w-full text-left">
            <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase bg-[#0d0e11] sticky top-0">
              <tr><th className="px-3 py-2">Name</th><th className="px-2">Username</th><th className="px-2">Badge</th><th className="px-2">Rank</th><th className="px-2">Role</th><th className="px-2">Access Code</th><th className="px-2">Status</th><th className="px-2"></th></tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`admin-user-${u.email}`} className="border-b border-[#2C2D35] hover:bg-[#141518]">
                  <td className="px-3 py-2 text-sm font-head">
                    {editId === u.id ? (
                      <div className="flex items-center gap-1">
                        <input data-testid={`admin-edit-name-input-${u.email}`} value={editName} onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveName(u.id); if (e.key === "Escape") setEditId(null); }}
                          className="bg-[#141518] border border-[#3B82F6] px-1.5 py-1 text-sm font-head outline-none w-40" autoFocus />
                        <button data-testid={`admin-save-name-${u.email}`} onClick={() => saveName(u.id)} title="Save" className="text-[#10B981] hover:text-[#22C55E]"><Check size={14} /></button>
                        <button data-testid={`admin-cancel-name-${u.email}`} onClick={() => setEditId(null)} title="Cancel" className="text-[#9CA3AF] hover:text-[#EF4444]"><X size={14} /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span>{u.name}</span>
                        {u.env === "training" && <span data-testid={`env-tag-${u.email}`} className="font-mono text-[8px] font-bold text-[#F59E0B] border border-[#F59E0B]/50 px-1 py-0.5">TRAINING</span>}
                        <button data-testid={`admin-edit-name-${u.email}`} onClick={() => startEdit(u)} title="Edit name" className="text-[#6B7280] hover:text-[#3B82F6]"><Pencil size={12} /></button>
                      </div>
                    )}
                    <div className="font-mono text-[10px] text-[#6B7280]">{u.agency}</div>
                  </td>
                  <td className="px-2 font-mono text-[11px] text-[#22D3EE]">@{u.username || "—"}</td>
                  <td className="px-2 font-mono text-[11px]">
                    <span className="inline-flex items-center gap-1">
                      {u.badge || "—"}
                      <button data-testid={`admin-rename-callsign-${u.email}`} onClick={() => renameCallsign(u)} title="Change call sign / unit ID (cascades to unit, calls, messages, GPS)" className="text-[#6B7280] hover:text-[#22D3EE]"><Pencil size={11} /></button>
                    </span>
                  </td>
                  <td className="px-2">
                    <select value={u.rank || ""} onChange={(e) => changeRank(u.id, e.target.value)} data-testid={`admin-rank-select-${u.email}`}
                      className="bg-[#141518] border border-[#2C2D35] px-1.5 py-1 text-[11px] font-mono outline-none text-[#E0B23C]">
                      <option value="" style={{ color: "#9CA3AF" }}>—</option>
                      {RANKS.map((r) => <option key={r} value={r} style={{ color: "#F3F4F6" }}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-2">
                    <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} data-testid={`admin-role-select-${u.email}`}
                      className="bg-[#141518] border border-[#2C2D35] px-1.5 py-1 text-[11px] font-mono outline-none" style={{ color: ROLE_COLOR[u.role] || "#F3F4F6" }}>
                      {roles.map((r) => <option key={r} value={r} style={{ color: "#F3F4F6" }}>{r.replace(/_/g, " ")}</option>)}
                    </select>
                  </td>
                  <td className="px-2">
                    <div className="flex items-center gap-1.5">
                      {u.initial_access_code
                        ? <button data-testid={`code-${u.email}`} onClick={() => copyCode(u.initial_access_code)} title="Click to copy" className="font-mono text-[12px] font-bold text-[#F59E0B] tracking-wider hover:underline">{u.initial_access_code}</button>
                        : <span className="font-mono text-[10px] text-[#6B7280]">—</span>}
                      <button data-testid={`regen-code-${u.email}`} onClick={() => regenCode(u.id, u.name)} title="Generate new 7-digit code" className="text-[#9CA3AF] hover:text-[#F59E0B]"><KeyRound size={12} /></button>
                    </div>
                  </td>
                  <td className="px-2">
                    <span className={`font-mono text-[10px] font-bold ${u.active === false ? "text-[#EF4444]" : "text-[#10B981]"}`}>{u.active === false ? "DISABLED" : "ACTIVE"}</span>
                  </td>
                  <td className="px-2">
                    {u.active === false ? (
                      <button data-testid={`admin-enable-${u.email}`} onClick={() => setActive(u.id, true)} className="flex items-center gap-1 px-2 py-1 text-[11px] font-head border border-[#2C2D35] hover:border-[#10B981] hover:text-[#10B981]"><CheckCircle2 size={12} /> Enable</button>
                    ) : (
                      <button data-testid={`admin-disable-${u.email}`} onClick={() => setActive(u.id, false)} className="flex items-center gap-1 px-2 py-1 text-[11px] font-head border border-[#2C2D35] hover:border-[#EF4444] hover:text-[#EF4444]"><Ban size={12} /> Disable</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="px-4 pb-6 pt-4 border-t border-[#2C2D35]">
        <FacilitiesAdmin />
      </div>
      </div>
    </Layout>
  );
}
